import { Component, ErrorInfo, ReactNode } from 'react'

interface Props {
  children: ReactNode
}
interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Global error boundary — prevents an uncaught render error from blanking the
 * whole SPA. Shows a recoverable 500-style state with a retry and a home link.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Logged for diagnostics; never exposed to end users.
    console.error('ErrorBoundary caught:', error, info)
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div role="alert" className="min-h-screen flex items-center justify-center bg-snow-50 p-6">
          <div className="max-w-lg w-full text-center">
            <div className="mx-auto mb-6 h-16 w-16 rounded-2xl bg-danger-50 flex items-center justify-center text-3xl" aria-hidden="true">
              ⚠️
            </div>
            <h1 className="text-2xl font-bold text-snow-900 mb-2">Something went wrong</h1>
            <p className="text-snow-600 mb-6">
              An unexpected error occurred while loading this page. You can try again, or go back to
              the home page.
            </p>
            <div className="flex gap-3 justify-center">
              <button onClick={this.handleRetry} className="btn-primary">
                Try again
              </button>
              <a href="/" className="btn-outline">
                Go to home
              </a>
            </div>
            {this.state.error && (
              <p className="mt-6 text-xs text-snow-400 font-mono break-all">
                {this.state.error.message}
              </p>
            )}
          </div>
        </div>
      )
    }
    return this.props.children
  }
}